/**
 * McpLimitRequests — solicitudes de aumento de límites MCP pendientes de decisión.
 */
import { useCallback, useEffect, useState } from 'react';
import { Inbox, Loader2, Check, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../services/adminService';

const LABELS = {
  max_nodes_per_project: 'Máx. nodos por proyecto',
  max_credits_per_run: 'Créditos por run',
  max_credits_per_day: 'Créditos por día',
  max_concurrent_runs: 'Runs simultáneos',
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('es', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
}) : '—');

export default function McpLimitRequests({ onApproved }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      setState(await adminService.listMcpLimitRequests('pending'));
    } catch (e) {
      setState({ requests: [], error: true });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (req, approve) => {
    setBusy(req.id);
    try {
      const res = await adminService.decideMcpLimitRequest(req.id, { approve });
      toast.success(approve
        ? `Límite actualizado para ${req.user_id}`
        : 'Solicitud rechazada');
      if (approve && onApproved) onApproved(req.user_id, res.limits);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
        || e.response?.data?.detail
        || 'No se pudo procesar la solicitud');
    } finally { setBusy(''); }
  };

  if (!state) return null;
  if (state.table_missing) {
    return (
      <div className="admin-mcp-warn" data-testid="mcp-limit-requests-table-missing">
        <AlertTriangle size={14} />
        Falta la tabla <code>mcp_limit_requests</code>: corré la migración
        <code>031_mcp_limit_requests.sql</code> en Supabase.
      </div>
    );
  }
  if (!state.requests?.length) return null;

  return (
    <div className="admin-mcp-requests" data-testid="admin-mcp-limit-requests">
      <div className="admin-mcp-requests-head">
        <Inbox size={14} />
        <strong>Solicitudes de aumento de límites ({state.requests.length})</strong>
      </div>
      {state.requests.map((r) => (
        <div className="admin-mcp-request" key={r.id} data-testid={`admin-mcp-request-${r.id}`}>
          <div className="admin-mcp-request-main">
            <strong>{r.email || r.user_id}</strong>
            <span>
              {LABELS[r.limit_key] || r.limit_key}: {r.current_value ?? '—'} → {r.requested_value}
            </span>
            {r.reason && <em>“{r.reason}”</em>}
            <small>{fmt(r.created_at)}</small>
          </div>
          <button
            className="admin-btn admin-btn-grant"
            onClick={() => decide(r, true)}
            disabled={busy === r.id}
            data-testid={`admin-mcp-request-approve-${r.id}`}
          >
            {busy === r.id ? <Loader2 size={12} className="spin" /> : <Check size={12} />} Aprobar
          </button>
          <button
            className="admin-btn admin-btn-deduct"
            onClick={() => decide(r, false)}
            disabled={busy === r.id}
            data-testid={`admin-mcp-request-reject-${r.id}`}
          >
            <X size={12} /> Rechazar
          </button>
        </div>
      ))}
    </div>
  );
}
